import { useAuth0 } from '@auth0/auth0-react';
import {
  Alert,
  Box,
  Button,
  Card,
  Center,
  Container,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconBrandFacebook,
  IconBrandGithub,
  IconBrandGoogle,
  IconBrandTwitter,
  IconCheck,
  IconLink,
  IconMail,
  IconSparkles,
  IconUser,
  IconUserPlus,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { fetchLinkableIdentities } from '../../api/user';
import { useUserStore } from '../../stores/userStore';
import type { LinkableIdentity } from '../../types';
import { handleUserApiError } from '../../utils/errorHandler';

// Provider icons mapping
const getProviderIcon = (provider: string) => {
  const iconProps = { size: 20 };

  switch (provider) {
    case 'google-oauth2':
      return <IconBrandGoogle {...iconProps} color="var(--mantine-color-red-6)" />;
    case 'auth0':
      return <IconMail {...iconProps} color="var(--mantine-color-blue-6)" />;
    case 'github':
      return <IconBrandGithub {...iconProps} color="var(--mantine-color-dark-6)" />;
    case 'facebook':
      return <IconBrandFacebook {...iconProps} color="var(--mantine-color-blue-6)" />;
    case 'twitter':
      return <IconBrandTwitter {...iconProps} color="var(--mantine-color-blue-5)" />;
    default:
      return <IconUser {...iconProps} color="var(--mantine-color-gray-6)" />;
  }
};

const getProviderName = (provider: string) => {
  switch (provider) {
    case 'google-oauth2':
      return 'Google';
    case 'auth0':
      return 'Email/Password';
    case 'github':
      return 'GitHub';
    case 'facebook':
      return 'Facebook';
    case 'twitter':
      return 'Twitter';
    default:
      return provider;
  }
};

export default function IdentityLinkingPage() {
  const { getAccessTokenSilently } = useAuth0();
  const { linkIdentity: linkIdentityInStore } = useUserStore();
  const [linkableIdentities, setLinkableIdentities] = useState<LinkableIdentity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  // Fetch identities once on mount
  useEffect(() => {
    const fetchIdentities = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const identities = await fetchLinkableIdentities(getAccessTokenSilently);
        setLinkableIdentities(identities);
      } catch (error: any) {
        console.error('Error fetching linkable identities:', error);
        handleUserApiError(error, 'fetching linkable identities');
        setError('Failed to fetch linkable identities');
      } finally {
        setIsLoading(false);
      }
    };

    fetchIdentities();
  }, [getAccessTokenSilently]);

  const handleLinkIdentity = async (auth0Sub: string) => {
    setError(null);
    setLoadingAction(auth0Sub);

    try {
      await linkIdentityInStore(getAccessTokenSilently, 'link', auth0Sub);
    } catch (error) {
      console.error('Error linking identity:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleCreateNewAccount = async () => {
    setError(null);
    setLoadingAction('nolink');

    try {
      await linkIdentityInStore(getAccessTokenSilently, 'nolink');
    } catch (error) {
      console.error('Error creating new account:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <Container size="sm" py="xl">
      <Box py="xl">
        <Container size="sm" />
      </Box>
      <Paper shadow="md" p="xl" radius="md">
        <Stack align="center" gap="lg">
          <Center>
            <Image src="/data/logo.svg" alt="Logo" width={128} height={128} />
          </Center>
          <Title order={1} ta="center" size="h2">
            You're Almost Ready!
          </Title>

          <IconLink size={64} color="var(--mantine-color-blue-6)" />

          <Text size="lg" ta="center" c="dimmed">
            We found existing accounts with the same email address. Would you like to link them together?
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" variant="light">
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Group>
              <Loader size="md" />
              <Text>Searching for accounts...</Text>
            </Group>
          ) : linkableIdentities.length === 0 ? (
            <Stack gap="md" align="center">
              <Text ta="center" c="dimmed">
                No existing accounts found with your email address.
              </Text>
              <Button
                size="lg"
                variant="filled"
                onClick={handleCreateNewAccount}
                disabled={loadingAction !== null}
                leftSection={loadingAction === 'nolink' ? <Loader size={16} /> : <IconUserPlus size={16} />}
              >
                {loadingAction === 'nolink' ? 'Creating...' : 'Create New Account'}
              </Button>
            </Stack>
          ) : (
            <Stack w="100%" gap="md">
              {linkableIdentities.map((identity: LinkableIdentity) => (
                <Card key={identity.auth0_sub} shadow="sm" padding="lg" radius="md" withBorder>
                  <Group justify="space-between" align="center">
                    <Group>
                      {getProviderIcon(identity.provider)}
                      <Stack gap="xs">
                        <Group gap="xs">
                          <Text fw={600}>{identity.username}</Text>
                        </Group>
                        <Text size="sm" c="dimmed">
                          {getProviderName(identity.provider)} • {identity.email}
                        </Text>
                      </Stack>
                    </Group>

                    <Button
                      variant="filled"
                      onClick={() => handleLinkIdentity(identity.auth0_sub)}
                      disabled={loadingAction !== null}
                      leftSection={
                        loadingAction === identity.auth0_sub ? <Loader size={16} /> : <IconCheck size={16} />
                      }
                    >
                      {loadingAction === identity.auth0_sub ? 'Linking...' : 'Link to this account'}
                    </Button>
                  </Group>
                </Card>
              ))}

              <Card shadow="sm" padding="lg" radius="md" withBorder bg="var(--mantine-color-gray-0)">
                <Group justify="space-between" align="center">
                  <Group>
                    <IconSparkles size={20} color="var(--mantine-color-yellow-6)" />
                    <Stack gap="xs">
                      <Text fw={600}>Create new account instead</Text>
                      <Text size="sm" c="dimmed">
                        Start fresh with a new account
                      </Text>
                    </Stack>
                  </Group>

                  <Button
                    variant="outline"
                    onClick={handleCreateNewAccount}
                    disabled={loadingAction !== null}
                    leftSection={loadingAction === 'nolink' ? <Loader size={16} /> : <IconUserPlus size={16} />}
                  >
                    {loadingAction === 'nolink' ? 'Creating...' : 'Create New Account'}
                  </Button>
                </Group>
              </Card>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
